import { Pipe, PipeTransform } from '@angular/core';

/**
 * Pipe for converting and formatting carbon footprint values
 * Converts kgCO2e to MgCO2e and formats the output with units
 *
 * Usage:
 *   {{ carbonFootprintKg | carbonFootprint }}
 *   {{ carbonFootprintKg | carbonFootprint:'Mg' }}
 *   {{ carbonFootprintKg | carbonFootprint:'MgCO₂e' }}
 *
 * @example
 *   {{ 1500 | carbonFootprint }} // Output: "1.500 Mg"
 *   {{ 2345.678 | carbonFootprint:'MgCO₂e' }} // Output: "2.346 MgCO₂e"
 */
@Pipe({
  name: 'carbonFootprint',
  standalone: false
})
export class CarbonFootprintPipe implements PipeTransform {
  /**
   * Convert carbon footprint from kgCO2e to Mg and format with units
   *
   * @param kgCO2e Carbon footprint in kilograms of CO2 equivalent
   * @param unit Unit suffix to append (default: 'Mg')
   * @param decimals Number of decimal places (default: 3)
   * @returns Formatted carbon footprint string with units, or '-' if null/undefined
   */
  transform(
    kgCO2e: number | null | undefined,
    unit: 'Mg' | 'MgCO₂e' | 'MgCO2e' = 'Mg',
    decimals: number = 3
  ): string {
    if (kgCO2e === null || kgCO2e === undefined) {
      return '-';
    }

    const mgCO2e = this.convertKgToMg(kgCO2e);
    return `${mgCO2e.toFixed(decimals)} ${unit}`;
  }

  /**
   * Convert carbon footprint from kgCO2e to Mg
   *
   * @param kgCO2e Carbon footprint in kilograms
   * @returns Carbon footprint in megagrams (tonnes)
   */
  private convertKgToMg(kgCO2e: number): number {
    return kgCO2e / 1000;
  }
}
